/**
 * Incremental Sync System (LEGACY)
 * 
 *  DEPRECATED: This file is being phased out in favor of dexieFirebaseSync.ts
 * - The main sync functions now redirect to Dexie-based sync
 * - detectStaleData and clearSyncTimestamps are still actively used
 * 
 * TODO: Remove this file once all references are migrated to dexieFirebaseSync.ts
 */

import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import type { SessionLog } from './sessionStorage';
import type { RecoveryMetrics } from './types/recovery';
import { storage } from './storage';

/**
 * Get last sync timestamp for a data type
 */
function getLastSyncTimestamp(dataType: 'sessions' | 'recovery' | 'assessments'): Date {
  if (typeof window === 'undefined') {
    return new Date(0); // Beginning of time
  }

  const key = `lastSync_${dataType}`;
  const stored = storage.get(key);
  
  if (stored) {
    return new Date(stored);
  }
  
  // If no last sync, check if we have data and use the latest date
  try {
    if (dataType === 'sessions') {
      const sessions = storage.get('sessionHistory');
      if (sessions) {
        const parsed = JSON.parse(sessions);
        if (parsed.length > 0) {
          const latest = parsed.reduce((max: any, s: any) => {
            const date = new Date(s.date);
            return date > max ? date : max;
          }, new Date(0));
          return latest;
        }
      }
    } else if (dataType === 'recovery') {
      const recovery = storage.get('recoveryHistory');
      if (recovery) {
        const parsed = JSON.parse(recovery);
        if (parsed.length > 0) {
          const latest = parsed.reduce((max: any, r: any) => {
            const date = new Date(r.date);
            return date > max ? date : max;
          }, new Date(0));
          return latest;
        }
      }
    }
  } catch (error) {
    console.error(`Error determining last sync for ${dataType}:`, error);
  }
  
  return new Date(0); // Default to beginning of time
}

/**
 * Update last sync timestamp
 */
function setLastSyncTimestamp(dataType: 'sessions' | 'recovery' | 'assessments'): void {
  if (typeof window === 'undefined') return;
  
  const key = `lastSync_${dataType}`;
  storage.set(key, new Date().toISOString());
}

/**
 * @deprecated Use dexieFirebaseSync.syncAll() instead
 * 
 * Incrementally sync sessions - now redirects to Dexie-based sync
 */
export async function incrementalSyncSessions(userId: string): Promise<{
  newSessions: number;
  totalSessions: number;
}> {
  console.warn(' incrementalSyncSessions is deprecated - using Dexie sync');
  
  try {
    const { syncAll } = await import('./dexieFirebaseSync');
    const result = await syncAll(userId);
    return {
      newSessions: result.downloaded.sessions,
      totalSessions: result.downloaded.sessions
    };
  } catch (error) {
    console.error(' Error syncing sessions:', error);
    throw error;
  }
}

/**
 * @deprecated Use dexieFirebaseSync.syncAll() instead
 * 
 * Incrementally sync recovery - now redirects to Dexie-based sync
 */
export async function incrementalSyncRecovery(userId: string): Promise<{
  newEntries: number;
  totalEntries: number;
}> {
  console.warn(' incrementalSyncRecovery is deprecated - using Dexie sync');
  
  try {
    const { syncAll } = await import('./dexieFirebaseSync');
    const result = await syncAll(userId);
    return {
      newEntries: result.downloaded.recovery,
      totalEntries: result.downloaded.recovery
    };
  } catch (error) {
    console.error(' Error syncing recovery:', error);
    throw error;
  }
}

/**
 * Sync all data incrementally
 */
export async function incrementalSyncAll(userId: string): Promise<{
  sessions: { newSessions: number; totalSessions: number };
  recovery: { newEntries: number; totalEntries: number };
}> {
  console.log('� Starting incremental sync...');
  
  const [sessions, recovery] = await Promise.all([
    incrementalSyncSessions(userId),
    incrementalSyncRecovery(userId)
  ]);
  
  console.log(' Incremental sync complete');
  
  return { sessions, recovery };
}

/**
 * Force full sync (ignores last sync timestamp)
 *  REFACTORED: Now uses Dexie-based sync
 * Use this for initial load or when user explicitly requests refresh
 */
export async function forceFullSync(userId: string, _limitCount: number = 50): Promise<void> {
  console.log('� Force full sync requested (using Dexie)...');
  
  // Clear last sync timestamps
  if (typeof window !== 'undefined') {
    storage.remove('lastSync_sessions');
    storage.remove('lastSync_recovery');
    storage.remove('lastSync_assessments');
  }
  
  // Use Dexie-based full download
  const { forceFullDownloadFromFirebase } = await import('./dexieFirebaseSync');
  const stats = await forceFullDownloadFromFirebase(userId);
  
  // Update legacy sync timestamps for backwards compatibility
  setLastSyncTimestamp('sessions');
  setLastSyncTimestamp('recovery');
  
  console.log(` Full sync complete: ${stats.sessions} sessions, ${stats.recovery} recovery entries`);
}

/**
 * Clear sync timestamps to force a full resync on next load
 * Call this when data appears stale or user reports missing sessions
 */
export function clearSyncTimestamps(): void {
  if (typeof window === 'undefined') return;
  
  storage.remove('lastSync_sessions');
  storage.remove('lastSync_recovery');
  storage.remove('lastSync_assessments');
  console.log('� Sync timestamps cleared - will resync on next load');
}

/**
 * Check if local data appears stale and needs a full resync
 * Returns true if a full sync should be triggered
 */
export function detectStaleData(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    const sessionsStr = storage.get('sessionHistory');
    const sessions = sessionsStr ? JSON.parse(sessionsStr) : [];
    
    if (sessions.length === 0) return false;
    
    // Find most recent session date
    const mostRecentSession = sessions.reduce((latest: Date, s: any) => {
      const date = new Date(s.date);
      return date > latest ? date : latest;
    }, new Date(0));
    
    // If most recent session is more than 7 days old, data might be stale
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    if (mostRecentSession < sevenDaysAgo) {
      console.log(' Local session data appears stale (most recent > 7 days old)');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking for stale data:', error);
    return false;
  }
}

